/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Button,
  Flex,
  Image,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  type PopoverProps,
  PopoverTrigger,
  Text,
} from '@chakra-ui/react';
import { IconArrowLeft } from 'central-icons/IconArrowLeft';
import type React from 'react';

import compositionImage from './tutorial-thumbnails/tutorial-compositions.jpg';
import dataEnrichmentImage from './tutorial-thumbnails/tutorial-data-enrichment.jpg';
import discoveryImage from './tutorial-thumbnails/tutorial-discovery.jpg';
import overviewImage from './tutorial-thumbnails/tutorial-overview.jpg';
import payImage from './tutorial-thumbnails/tutorial-pay.jpg';
import postingsImage from './tutorial-thumbnails/tutorial-postings.jpg';
import reportBuilderImage from './tutorial-thumbnails/tutorial-report-builder.jpg';
import sentimentImage from './tutorial-thumbnails/tutorial-sentiment.jpg';
import talentDiscoveryImage from './tutorial-thumbnails/tutorial-talent-discovery.jpg';
import transitionsImage from './tutorial-thumbnails/tutorial-transitions.jpg';

export type VideoData = {
  id: string;
  name: string;
  description: string;
  duration: string;
  thumbnail: string;
  mediaId: string;
  paths: string[];
};

export const videoMetadata: Array<{
  category: string;
  videos: VideoData[];
}> = [
  {
    category: 'Dashboard Pages',
    videos: [
      {
        name: 'Dashboard Overview',
        description:
          'Analyze workforces through the lens of Companies, Geographies, Roles, and People.',
        duration: '2 mins',
        thumbnail: overviewImage,
        mediaId: 'vxukqgd7v9',
        paths: ['/'],
        id: '0',
      },
      {
        name: 'Overview',
        description:
          'Review a high-level summary of an organization, geography, or role and its talent composition and growth rates.',
        duration: '2 mins',
        thumbnail: overviewImage,
        mediaId: 'ei8r9m9c82',
        paths: ['/company/summary', '/geography/summary', '/role/summary'],
        id: '11',
      },
      {
        name: 'Compositions',
        description:
          'Benchmark workforces and gather competitive intelligence.',
        duration: '1 min',
        thumbnail: compositionImage,
        mediaId: 'zhuzvcgm1n',
        paths: [
          '/company/compositions',
          '/geography/compositions',
          '/role/compositions',
        ],
        id: '1',
      },
      {
        name: 'Transitions',
        description: 'See where talent is coming from and going to.',
        duration: '1 min',
        thumbnail: transitionsImage,
        mediaId: 'l44r5f542f',
        paths: [
          '/company/transitions',
          '/geography/transitions',
          '/role/transitions',
        ],
        id: '2',
      },
      {
        name: 'Job Postings',
        description: 'Analyze demand for roles and skill.',
        duration: '1 min',
        thumbnail: postingsImage,
        mediaId: 'etp6wqmphy',
        paths: ['/company/postings', '/geography/postings', '/role/postings'],
        id: '3',
      },
      {
        name: 'Pay',
        description: 'Benchmark salary across companies, locations, and roles.',
        duration: '2 mins',
        thumbnail: payImage,
        mediaId: 'uuzhueff2l',
        paths: ['/company/pay', '/geography/pay', '/role/pay'],
        id: '4',
      },
      {
        name: 'Sentiment',
        description:
          'Reveal factors of hiring and attrition for specific workforces.',
        duration: '1 min',
        thumbnail: sentimentImage,
        mediaId: 'c5pz9z6vca',
        paths: [
          '/company/sentiment',
          '/geography/sentiment',
          '/role/sentiment',
        ],
        id: '5',
      },
      {
        name: 'Talent Discovery',
        description:
          'Discover individual candidates matching your unique search criteria.',
        duration: '3 mins',
        thumbnail: talentDiscoveryImage,
        mediaId: '5o614nymsh',
        paths: ['/talent-discovery'],
        id: '6',
      },
      {
        name: 'Discovery',
        description:
          'Discover companies, roles, and talent markets by growth rate, attrition rate, skill density, and more.',
        duration: '3 mins',
        thumbnail: discoveryImage,
        mediaId: 'xffre1dkvc',
        paths: ['/company/screener', '/geography/screener', '/role/screener'],
        id: '7',
      },
    ],
  },
  {
    category: 'Custom Reports & Datasets',
    videos: [
      {
        name: 'Report Builder',
        description:
          'Build a custom report from our template library of workforce planning use cases.',
        duration: '1 min',
        thumbnail: reportBuilderImage,
        mediaId: '6dou1fxe9r',
        paths: [
          '/report-builder/report_type',
          '/reports',
          '/reports/',
          '/report-builder/',
        ],
        id: '8',
      },
      {
        name: 'Data Enrichment',
        description:
          'Enrich your internal data. This advanced feature is available for trials with our sales team.',
        duration: '1 min',
        thumbnail: dataEnrichmentImage,
        mediaId: 'oqbg209tqf',
        paths: ['/deliverables/data-enrichment'],
        id: '10',
      },
    ],
  },
];

export interface TutorialProps
  extends Pick<PopoverProps, 'placement' | 'gutter'> {
  isOpen: boolean;
  onClose: () => void;
  // The trigger element for the popover (e.g., the Methodology menu)
  children: React.ReactElement;
  videoData: VideoData | null;
  onWatch: () => void;
  onContactSales: () => void;
  onBack?: () => void;
  onSeen?: () => void;
}

export const Tutorial = ({
  isOpen,
  onClose,
  children,
  videoData,
  onWatch,
  onContactSales,
  placement = 'right-end',
  gutter = 0,
  onBack,
  onSeen,
}: TutorialProps) => {
  if (!videoData) return children;

  return (
    <Popover
      placement={placement}
      variant="with-shadow"
      returnFocusOnClose={false}
      isOpen={isOpen}
      onClose={onClose}
      arrowSize={12}
      isLazy={false}
      autoFocus={false}
      gutter={gutter}
    >
      <PopoverTrigger>{children}</PopoverTrigger>
      <PopoverContent
        sx={{
          _focus: { boxShadow: 'none', outline: 'none' },
          _focusVisible: { boxShadow: 'none', outline: 'none' },
        }}
        variants={{
          exit: {
            scale: 0,
            opacity: 0,
          },
          enter: {
            scale: 1,
            opacity: 1,
          },
        }}
      >
        <PopoverArrow />
        {onBack && (
          <PopoverHeader pb={0} border="none">
            <Button
              size="xs"
              variant="ghost"
              colorScheme="gray"
              leftIcon={<IconArrowLeft size={16} />}
              textColor="text.primary"
              aria-label="back"
              onClick={onBack}
              ml={-1.5}
            >
              Methodology
            </Button>
          </PopoverHeader>
        )}
        <PopoverBody maxH="360px" pb={3}>
          <Flex direction="column" gap={2}>
            <Image
              src={videoData.thumbnail}
              alt="video thumbnail"
              rounded="xl"
              cursor="pointer"
              _hover={{ opacity: 0.8 }}
              onMouseEnter={onSeen}
              onClick={() => {
                onSeen?.();
                onWatch();
              }}
            />
            <Text fontSize="md" fontWeight="semibold" px={1} lineHeight="16px">
              {videoData.name}
            </Text>
            <Text fontSize="sm" px={1} lineHeight="16px">
              {videoData.description}
              {videoData.id === '10' && (
                <span>
                  {` `}
                  <Link
                    onClick={onContactSales}
                    color="green.500"
                    fontWeight={600}
                  >
                    Contact Sales
                  </Link>{' '}
                  to get started.
                </span>
              )}
            </Text>
            <Button
              size="lg"
              mt={2}
              colorScheme="text"
              onMouseEnter={onSeen}
              onClick={() => {
                onSeen?.();
                onWatch();
              }}
            >
              Watch Tutorial
              <span
                style={{
                  fontSize: '16px',
                  opacity: 0.7,
                  paddingLeft: '6px',
                  fontWeight: 500,
                }}
              >
                {videoData.duration}
              </span>
            </Button>
          </Flex>
        </PopoverBody>
        <PopoverCloseButton top={2} data-testid="tutorial-close-button" />
      </PopoverContent>
    </Popover>
  );
};
